import type {WhatwgUrlLike} from './product-url.js';

/**
 * VRG-104 — the rule for a `link` custom field's URL, in one place.
 *
 * It lives in `@repo/types` rather than in the API's validator because TWO sides need
 * the same answer and they cannot import each other: `apps/api` enforces it on the
 * write (the authoring gate), and `apps/platform`'s Custom fields card has to tell the
 * brand what is wrong WHILE they type and show the host the reader will see. A second
 * copy on the client is how the form comes to accept something the API rejects, or
 * rejects something it would have accepted — and the brand has no way to tell which
 * side is wrong.
 *
 * So the `reason` strings are deliberately user-facing: the form renders them as helper
 * text and the API returns them in its 400, which means a brand who submits anyway sees
 * the same sentence they already saw in the field.
 *
 * ⚠️ The CLIENT copy is a hint; the API is the gate. Nothing here is a security control
 * on its own — `apps/api`'s `normalizeProductCustomFields` is what actually refuses to
 * store a bad value, and `apps/app`'s sanitizer is what stops `rich` HTML carrying an
 * `<a>` around this check entirely.
 */

/** Reached through `globalThis` for the reason `WhatwgUrlLike` documents (no DOM lib here). */
const UrlCtor = (globalThis as unknown as {URL: new (input: string) => WhatwgUrlLike})
  .URL;

/**
 * ⚠️ NOT a discriminated union (`{ok: true, host} | {ok: false, reason}`), which is what
 * this wants to be and was written as first. `apps/api` compiles with
 * `strictNullChecks: false`, and in that mode TypeScript does not narrow a union by a
 * boolean-literal discriminant — `if (!check.ok) throw new Error(check.reason)` fails to
 * compile there with "Property 'reason' does not exist". A shared type has to be legal in
 * every consumer's own compiler settings, so both fields are always present and nullable:
 * `reason` set means unusable, `host` set means usable, and neither side needs narrowing.
 */
export interface CustomFieldLinkCheck {
  /**
   * The hostname shown to a certificate reader — WITHOUT a port, since it is read, not
   * dialled. `null` when the value is not a usable link.
   */
  host: string | null;
  /** User-facing reason the value cannot be used. `null` when the value is fine. */
  reason: string | null;
}

/** What a link field's `value` must start with. See `checkCustomFieldLink`. */
export const CUSTOM_FIELD_LINK_PROTOCOL = 'https:';

/**
 * `https` only, and absolute.
 *
 * `javascript:` and `data:` are the two that matter — both are accepted by a naive
 * "does it parse" check and both execute in the page. `http:` is rejected as well: a
 * certificate is served over TLS, so an `http` link is a mixed-content downgrade on a
 * page whose entire claim is that it can be trusted.
 */
export function checkCustomFieldLink(
  raw: string | null | undefined,
): CustomFieldLinkCheck {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return {host: null, reason: 'A link field needs a web address.'};
  }

  let url: WhatwgUrlLike;
  try {
    url = new UrlCtor(trimmed);
  } catch {
    return {
      host: null,
      reason:
        'A link field needs a full web address, starting with https:// — for example https://example.com/care',
    };
  }

  if (url.protocol !== CUSTOM_FIELD_LINK_PROTOCOL) {
    return {
      host: null,
      reason: `A link field must use https:// (got "${url.protocol}"). Links on a certificate are served over TLS.`,
    };
  }

  return {host: url.hostname, reason: null};
}

/**
 * The host a reader is shown next to a link, so they can see where it goes first —
 * `null` for anything that is not a valid link value.
 *
 * Returns rather than throws because the RENDER side calls it: a legacy or
 * hand-edited row must not take the certificate page down.
 */
export function customFieldLinkHost(raw: string | null | undefined): string | null {
  return checkCustomFieldLink(raw).host;
}
